import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { NAV } from "@/nav";
import { Menu, X, ChevronDown } from "lucide-react";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [openCluster, setOpenCluster] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 bg-paper/95 backdrop-blur-sm border-b transition-colors ${
        scrolled ? "border-line" : "border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <Link to="/" data-testid="site-logo" className="flex items-baseline gap-2 group">
          <span className="font-serif text-2xl tracking-tight text-ink">New Face</span>
          <span className="font-sans uppercase text-[10px] tracking-[0.28em] text-terracotta">Guide</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((cluster) => (
            <div
              key={cluster.key}
              className="relative"
              onMouseEnter={() => setOpenCluster(cluster.key)}
              onMouseLeave={() => setOpenCluster(null)}
            >
              <button
                data-testid={`nav-cluster-${cluster.key}`}
                className="nav-link text-sm font-medium tracking-wide inline-flex items-center gap-1 py-1"
                aria-haspopup="true"
                aria-expanded={openCluster === cluster.key}
              >
                {cluster.label}
                <ChevronDown size={14} className="opacity-60" />
              </button>
              {openCluster === cluster.key && (
                <div
                  className="absolute left-0 top-full pt-4 w-80"
                  role="menu"
                >
                  <div className="bg-paper border border-line p-6 shadow-sm animate-fade-up">
                    <p className="text-xs uppercase tracking-[0.24em] text-terracotta mb-3">{cluster.label}</p>
                    <p className="text-sm text-stone mb-5 leading-relaxed">{cluster.intro}</p>
                    <ul className="space-y-3">
                      {cluster.items.map((item) => (
                        <li key={item.slug}>
                          <Link
                            to={`/${item.slug}`}
                            data-testid={`nav-item-${item.slug}`}
                            className="text-sm text-ink hover:text-terracotta transition-colors"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 -mr-2 text-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-line bg-paper" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
            {NAV.map((cluster) => (
              <div key={cluster.key}>
                <p className="text-xs uppercase tracking-[0.24em] text-terracotta mb-3">{cluster.label}</p>
                <ul className="space-y-2 pl-1">
                  {cluster.items.map((item) => (
                    <li key={item.slug}>
                      <NavLink
                        to={`/${item.slug}`}
                        data-testid={`mobile-nav-item-${item.slug}`}
                        className="block py-1.5 text-ink hover:text-terracotta"
                        onClick={() => setOpen(false)}
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-line mt-24 bg-cream">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
      <div>
        <p className="font-serif text-2xl text-ink mb-3">New Face Guide</p>
        <p className="text-sm text-stone leading-relaxed max-w-xs">
          Independent, non-promotional guidance for newly scouted models and the parents
          reading over their shoulder. Nothing here is legal, financial, or immigration
          advice — always confirm with a qualified professional.
        </p>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.24em] text-terracotta mb-4">Sections</p>
        <ul className="space-y-2 text-sm">
          {NAV.map((c) => (
            <li key={c.key}>
              <Link to={`/${c.items[0].slug}`} className="prose-link" data-testid={`footer-link-${c.key}`}>
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.24em] text-terracotta mb-4">If something feels wrong</p>
        <p className="text-sm text-stone leading-relaxed mb-4">
          Immediate danger: call 999. Otherwise, the safeguarding page has a short list of
          the UK helplines worth knowing.
        </p>
        <Link
          to="/safeguarding-contacts"
          data-testid="footer-safeguarding-link"
          className="inline-block text-sm prose-link"
        >
          Open the safeguarding page →
        </Link>
      </div>
    </div>
    <div className="border-t border-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-stone">
        <p>© {new Date().getFullYear()} New Face Guide. Independent editorial resource.</p>
        <Link to="/admin/login" className="text-stone hover:text-terracotta transition-colors" data-testid="footer-admin-login">
          Editor sign-in
        </Link>
      </div>
    </div>
  </footer>
);

const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-paper text-ink">
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-ink focus:text-paper focus:px-3 focus:py-2 focus:z-50"
    >
      Skip to main content
    </a>
    <Header />
    <main id="main" className="flex-1">{children}</main>
    <Footer />
  </div>
);

export default Layout;
